import React, { useMemo } from 'react';
import {
  formatFechaCorta,
  pluralizarUnidad,
  resolvePlanDosificacionPedido,
  resolveRecetaPlanSlot
} from '../utils/tratamientoDosificacion';

function Metric({ label, value, sub }) {
  return (
    <div className="crm-re-plan__metric">
      <span className="crm-re-plan__metric-k">{label}</span>
      <strong className="crm-re-plan__metric-v">{value}</strong>
      {sub ? <span className="crm-re-plan__metric-sub">{sub}</span> : null}
    </div>
  );
}

function Timeline({ plan, highlightNumero = null, compact = false }) {
  if (!plan?.slots?.length) return null;
  return (
    <div className={`crm-re-plan__timeline${compact ? ' crm-re-plan__timeline--compact' : ''}`} role="list" aria-label="Cronograma de recetas">
      {plan.slots.map((slot) => {
        const active = highlightNumero != null && Number(slot.numero) === Number(highlightNumero);
        const emitted = slot.estado && slot.estado !== 'pendiente';
        return (
          <div
            key={slot.numero}
            role="listitem"
            className={`crm-re-plan__tl-item${active ? ' crm-re-plan__tl-item--active' : ''}${emitted ? ' crm-re-plan__tl-item--done' : ''}`}
          >
            <div className="crm-re-plan__tl-dot" aria-hidden="true">
              {emitted ? '✓' : slot.numero}
            </div>
            <div className="crm-re-plan__tl-body">
              <div className="crm-re-plan__tl-title">
                Receta {slot.numero}
                {active ? <span className="crm-re-plan__tl-badge">Actual</span> : null}
              </div>
              <div className="crm-re-plan__tl-dates">
                {formatFechaCorta(slot.fechaInicio)} → {formatFechaCorta(slot.fechaFin)}
              </div>
              {!compact ? (
                <div className="crm-re-plan__tl-meta">
                  {slot.duracionDias} días
                  {slot.unidades ? ` · ${slot.unidades} u.` : ''}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function TratamientoPlanResumen({
  pedido,
  receta = null,
  recetaNumero = null,
  recetasEstado = [],
  variant = 'group'
}) {
  const plan = useMemo(() => resolvePlanDosificacionPedido(pedido), [pedido]);
  const slot = useMemo(
    () => (recetaNumero != null ? resolveRecetaPlanSlot(plan, recetaNumero, receta) : null),
    [plan, recetaNumero, receta]
  );

  const timelinePlan = useMemo(() => {
    if (!plan.hasPlan) return null;
    const estadoByNum = new Map(
      (recetasEstado || []).map((r) => [Number(r.numero), r.estado])
    );
    const recetaByNum = new Map(
      (recetasEstado || []).map((r) => [Number(r.numero), r])
    );
    return {
      ...plan,
      slots: plan.slots.map((s) => {
        const rec = recetaByNum.get(s.numero);
        const merged = resolveRecetaPlanSlot(plan, s.numero, rec);
        return {
          ...s,
          ...merged,
          estado: estadoByNum.get(s.numero) || 'pendiente'
        };
      })
    };
  }, [plan, recetasEstado]);

  if (!plan.hasPlan) return null;

  const unidadTxt = plan.dosisPorToma
    ? pluralizarUnidad(plan.unidadDosis, plan.dosisPorToma)
    : pluralizarUnidad(plan.unidadDosis, 2);

  if (variant === 'compact') {
    return (
      <div className="crm-re-plan crm-re-plan--compact" aria-label="Plan de dosificación">
        {plan.dosisPorToma ? (
          <span className="crm-re-plan__pill">
            {plan.dosisPorToma} {unidadTxt} · {plan.frecuenciaLabel}
          </span>
        ) : null}
        {plan.unidadesPorReceta ? (
          <span className="crm-re-plan__pill crm-re-plan__pill--units">
            {plan.unidadesPorReceta} u./receta
          </span>
        ) : null}
        {plan.duracionRecetaDias ? (
          <span className="crm-re-plan__pill crm-re-plan__pill--days">
            {plan.duracionRecetaDias} d c/u
          </span>
        ) : null}
      </div>
    );
  }

  if (variant === 'receta' && slot) {
    return (
      <div className="crm-re-plan crm-re-plan--receta">
        <span className="crm-re-plan__receta-line">
          {formatFechaCorta(slot.fechaInicio)} → {formatFechaCorta(slot.fechaFin)}
        </span>
        <span className="crm-re-plan__receta-sep">·</span>
        <span className="crm-re-plan__receta-meta">{slot.duracionDias} d</span>
        {slot.unidades ? (
          <>
            <span className="crm-re-plan__receta-sep">·</span>
            <span className="crm-re-plan__receta-meta">{slot.unidades} {unidadTxt}</span>
          </>
        ) : null}
      </div>
    );
  }

  if (variant === 'modal') {
    return (
      <section className="crm-re-plan crm-re-plan--modal" aria-label="Plan de dosificación">
        <div className="crm-re-plan__modal-head">
          <div>
            <span className="crm-re-plan__eyebrow">Plan aprobado</span>
            <h3 className="crm-re-plan__title">
              {recetaNumero ? `Receta ${recetaNumero} del tratamiento` : 'Dosificación del tratamiento'}
            </h3>
          </div>
          <div className="crm-re-plan__icon" aria-hidden="true">💊</div>
        </div>

        <div className="crm-re-plan__metrics crm-re-plan__metrics--modal">
          <Metric
            label="Toma"
            value={`${plan.dosisPorToma ?? '—'} ${unidadTxt}`}
            sub={plan.frecuenciaLabel || undefined}
          />
          <Metric
            label="Por receta"
            value={plan.unidadesPorReceta ?? '—'}
            sub={plan.unidadesPorReceta ? unidadTxt : undefined}
          />
          <Metric
            label="Duración"
            value={plan.duracionRecetaDias ?? '—'}
            sub={plan.duracionRecetaDias ? 'días c/u' : undefined}
          />
          <Metric
            label="Consumo"
            value={plan.consumoDiario ?? '—'}
            sub={plan.consumoDiario ? `${unidadTxt}/día` : undefined}
          />
        </div>

        {slot ? (
          <div className="crm-re-plan__focus">
            <div className="crm-re-plan__focus-label">Cobertura de esta emisión</div>
            <div className="crm-re-plan__focus-range">
              <strong>{formatFechaCorta(slot.fechaInicio)}</strong>
              <span className="crm-re-plan__focus-arrow">→</span>
              <strong>{formatFechaCorta(slot.fechaFin)}</strong>
            </div>
            <div className="crm-re-plan__focus-meta">
              {slot.duracionDias} días de tratamiento
              {slot.unidades ? ` · dispensar ${slot.unidades} ${unidadTxt}` : ''}
            </div>
          </div>
        ) : null}

        {plan.posologia ? (
          <p className="crm-re-plan__posologia">
            <span>Posología:</span> {plan.posologia}
          </p>
        ) : null}

        {timelinePlan && timelinePlan.slots.length > 1 ? (
          <Timeline plan={timelinePlan} highlightNumero={recetaNumero} compact />
        ) : null}
      </section>
    );
  }

  return null;
}
